import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { useCart } from "@/context/CartContext";
import { FEATURED_IDS } from "@/components/PopularProducts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];

const FIRST_RECOMMENDED_ID = "portable-led-tablet-book-light-reading-night-light";

const fmt = (price) =>
  `$${Number.isInteger(price) ? price : price.toFixed(2)}`;

export default function RecommendedCarousel() {
  const { data: products = [] } = useQuery({
    queryKey: ["products"],
    queryFn: async () => (await axios.get(`${API}/products`)).data,
  });
  const { add, setOpen } = useCart();
  const navigate = useNavigate();

  const exclude = new Set([...FEATURED_IDS, products[0]?.id]);
  const recommended = products
    .filter((p) => !exclude.has(p.id))
    .sort((a, b) =>
      a.id === FIRST_RECOMMENDED_ID ? -1 : b.id === FIRST_RECOMMENDED_ID ? 1 : 0
    )
    .slice(0, 10);
  if (!recommended.length) return null;

  const handleAdd = (product) => {
    add(product);
    toast(`${product.name} added to your bag`, {
      action: { label: "View Bag", onClick: () => setOpen(true) },
    });
  };

  return (
    <section
      data-testid="recommended-section"
      className="border-y border-line bg-stone/60 py-10 md:py-24"
    >
      <div className="mx-auto max-w-[1600px] px-5 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: EASE }}
        >
          <Carousel opts={{ align: "start", dragFree: true }}>
            <div className="mb-6 flex items-end justify-between gap-4 md:mb-10">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-smoke">
                  Recommended
                </p>
                <h2 className="mt-2 font-serif text-3xl font-medium tracking-tight text-ink sm:text-4xl md:mt-4 md:text-6xl">
                  Swipe the <span className="italic text-ember">good</span> stuff.
                </h2>
              </div>
              <div className="hidden gap-3 md:flex">
                <CarouselPrevious
                  data-testid="recommended-prev"
                  className="static h-12 w-12 translate-y-0 rounded-full border-line bg-transparent text-ink hover:bg-ember hover:text-ink"
                />
                <CarouselNext
                  data-testid="recommended-next"
                  className="static h-12 w-12 translate-y-0 rounded-full border-line bg-transparent text-ink hover:bg-ember hover:text-ink"
                />
              </div>
            </div>

            <CarouselContent className="-ml-4">
              {recommended.map((product, i) => (
                <CarouselItem
                  key={product.id}
                  className="basis-[82%] pl-4 sm:basis-[46%] lg:basis-[31%] xl:basis-[24%]"
                >
                  <div
                    data-testid={`recommended-card-${product.id}`}
                    className="group"
                  >
                    <div
                      className="relative aspect-square cursor-pointer overflow-hidden bg-white"
                      onClick={() => navigate(`/product/${product.id}`)}
                      data-testid={`recommended-view-${product.id}`}
                    >
                      <img
                        src={product.image}
                        alt={product.name}
                        loading="lazy"
                        decoding="async"
                        className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:rotate-1 group-hover:scale-105"
                      />
                      <span className="absolute left-3 top-3 bg-ember px-2 py-0.5 text-[9px] uppercase tracking-[0.2em] text-ink sm:left-4 sm:top-4 sm:px-3 sm:py-1 sm:text-[10px]">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <div className="mt-3 flex items-start justify-between gap-2 md:mt-4 md:gap-3">
                      <h3
                        className="line-clamp-2 cursor-pointer font-serif text-lg font-medium leading-snug tracking-tight text-ink transition-colors duration-300 hover:text-ember sm:text-xl"
                        onClick={() => navigate(`/product/${product.id}`)}
                      >
                        {product.name}
                      </h3>
                      <span className="pt-0.5 text-xs font-medium text-ink sm:pt-1 sm:text-sm">
                        {fmt(product.price)}
                      </span>
                    </div>
                    {product.available === false ? (
                      <div
                        data-testid={`sold-out-${product.id}`}
                        className="mt-2.5 w-full border border-line py-2 text-center text-[10px] uppercase tracking-[0.25em] text-smoke sm:mt-3 sm:py-2.5 sm:text-[11px]"
                      >
                        Sold Out
                      </div>
                    ) : (
                      <button
                        data-testid={`recommended-add-${product.id}`}
                        onClick={() => handleAdd(product)}
                        className="mt-2.5 flex w-full items-center justify-center gap-2 border border-ink py-2 text-[10px] uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:border-ember hover:bg-ember sm:mt-3 sm:py-2.5 sm:text-[11px]"
                      >
                        <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
                        Add to Bag
                      </button>
                    )}
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        </motion.div>
      </div>
    </section>
  );
}
